import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Menu,
  MenuItem,
  CircularProgress,
  useTheme,
} from "@mui/material";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
const CustomTooltip = ({ active, payload, label, benchmarkNames, chartData, coordinate, viewBox }) => {
  const theme = useTheme();

  if (!active || !payload || !payload.length || !chartData) {
    return null;
  }

  const currentIndex = chartData.findIndex(d => d.date === label);
  const prevData = currentIndex > 0 ? chartData[currentIndex - 1] : null;

  const chartWidth = viewBox?.width || 800;
  const isNearRightEdge = coordinate && coordinate.x > chartWidth * 0.75;
  
  const tooltipStyle = isNearRightEdge
    ? {
        transform: 'translateX(calc(-100% - 12px))',
        marginLeft: '0',
      }
    : {};

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        padding: 1.5,
        boxShadow: theme.shadows[3],
        maxWidth: '300px',
        ...tooltipStyle,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          marginBottom: 1,
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>

      {payload.map((entry, index) => {
        const dataKey = entry.dataKey; // "Strategy" or "QQQ"
        const currentValue = Number(entry.value);

        const prevValue =
          prevData && prevData[dataKey] != null
            ? Number(prevData[dataKey])
            : null;

        let percentage = 0;
        let isPositive = true;

        if (prevValue !== null && prevValue !== 0 && !isNaN(prevValue)) {
          percentage = ((currentValue - prevValue) / prevValue) * 100;
          isPositive = percentage >= 0;
        }

        const displayName =
          dataKey === "Strategy"
            ? "Strategy"
            : dataKey === "Long"
            ? "Long"
            : dataKey === "Short"
            ? "Short"
            : benchmarkNames?.[dataKey] || dataKey;

       
      


        return (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              marginTop: 0.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color:
                  dataKey === "Strategy"
                    ? theme.palette.primary.main
                    : dataKey === "Long"
                    ? theme.palette.grey[600]
                    : dataKey === "Short"
                    ? theme.palette.grey[400]
                    : theme.palette.secondary?.main || theme.palette.info.main,
                minWidth: "100px",
                fontWeight: 500,
              }}
            >
              {displayName}:
            </Typography>

            {prevValue !== null ? (
              <Typography
                variant="body2"
                sx={{
                  color: isPositive
                    ? theme.palette.success.main
                    : theme.palette.error.main,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.25,
                }}
              >
                {isPositive ? (
                  <ArrowDropUpIcon sx={{ fontSize: 28 }} />
                ) : (
                  <ArrowDropDownIcon sx={{ fontSize: 28 }} />
                )}
                {Math.abs(percentage).toFixed(2)}%
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                —
              </Typography>
            )}

            <Typography
              variant="body2"
              sx={{ marginLeft: 0.5 }}
            >
              {currentValue.toFixed(4)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};


const StrategyPerformanceChart = ({
  strategyVal,
  chartData,
  benchmark,
  setBenchmark,
  benchmarkNames,
  yScale,
  setYScale,
  yAxisDomain,
  activeRequests,
}) => {
  const theme = useTheme();
  const [benchmarkAnchorEl, setBenchmarkAnchorEl] = useState(null);
  
  // Check if Long and Short data exists in chartData
  const hasLongData = chartData.some(d => d.Long !== undefined && d.Long !== null);
  const hasShortData = chartData.some(d => d.Short !== undefined && d.Short !== null);

  if (!strategyVal) {
    return (
      <Card
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardContent>
          <Box
            sx={{
              height: 400,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
              }}
            >
              Please select a strategy to view performance data
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (activeRequests.includes("get_historical_signal_set")) {
    return (
      <Card
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardContent>
          <Box
            sx={{
              height: 400,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <CircularProgress size={40} />
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
              }}
            >
              Fetching strategy performance for {strategyVal}...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!chartData.length) {
    return (
      <Card
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardContent>
          <Box
            sx={{
              height: 400,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
              }}
            >
              No data available for the selected period
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                opacity: 0.7,
              }}
            >
              Try selecting a different period or date range
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <CardContent>
        {/* Header row with top-right toggle */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              color: theme.palette.text.primary,
            }}
          >
            Performance Chart
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              disabled={!strategyVal}
              onClick={(e) => setBenchmarkAnchorEl(e.currentTarget)}
            >
              Benchmark:{" "}
              {benchmark ? benchmarkNames[benchmark] : "None"}
            </Button>
            <Menu
              anchorEl={benchmarkAnchorEl}
              open={Boolean(benchmarkAnchorEl)}
              onClose={() => setBenchmarkAnchorEl(null)}
            >
              <MenuItem
                onClick={() => {
                  setBenchmark("QQQ");
                  setBenchmarkAnchorEl(null);
                }}
              >
                Nasdaq 100
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setBenchmark("SPY");
                  setBenchmarkAnchorEl(null);
                }}
              >
                S&P 500
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setBenchmark("IWM");
                  setBenchmarkAnchorEl(null);
                }}
              >
                Russell 2000
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setBenchmark("IWV");
                  setBenchmarkAnchorEl(null);
                }}
              >
                Russell 3000
              </MenuItem>
            </Menu>
            <Button
              variant="outlined"
              size="small"
              disabled={!strategyVal}
              onClick={() =>
                setYScale((s) => (s === "linear" ? "log" : "linear"))
              }
            >
              Scale:{" "}
              {yScale === "linear" ? "Arithmetic" : "Logarithmic"}
            </Button>
          </Box>
        </Box>

        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={theme.palette.divider}
              opacity={0.3}
              vertical={false}
            />
            <XAxis
              dataKey="date"
              stroke={theme.palette.text.secondary}
              tick={{
                fill: theme.palette.text.secondary,
                fontSize: 12,
              }}
              tickLine={{ stroke: theme.palette.text.secondary }}
              axisLine={{ stroke: theme.palette.divider }}
              interval="preserveStartEnd"
            />
            <YAxis
              scale={yScale}
              domain={yAxisDomain}
              stroke={theme.palette.text.secondary}
              tick={{
                fill: theme.palette.text.secondary,
                fontSize: 12,
              }}
              tickLine={{ stroke: theme.palette.text.secondary }}
              axisLine={{ stroke: theme.palette.divider }}
              tickFormatter={(value) => `${Number(value).toFixed(2)}`}
            />
            <Tooltip
              content={<CustomTooltip benchmarkNames={benchmarkNames} chartData={chartData} />}
              allowEscapeViewBox={{ x: true, y: false }}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="Strategy"
              name="Strategy"
              stroke={theme.palette.primary.main}
              strokeWidth={2}
              strokeOpacity={0.7}
              dot={false}
              activeDot={{ r: 5 }}
              connectNulls
            />
            {benchmark && (
              <Line
                type="monotone"
                dataKey={benchmark}
                name={benchmarkNames[benchmark] || benchmark}
                stroke={
                  theme.palette.secondary?.main || theme.palette.info.main
                }
                strokeWidth={2}
                strokeOpacity={0.7}
                dot={false}
                activeDot={{ r: 5 }}
                connectNulls
              />
            )}
            {hasLongData && (
              <Line
                type="monotone"
                dataKey="Long"
                name="Long"
                stroke={theme.palette.grey[600]}
                strokeWidth={1.5}
                strokeOpacity={0.5}
                dot={false}
                activeDot={{ r: 4 }}
                connectNulls
                strokeDasharray="5 5"
              />
            )}
            {hasShortData && (
              <Line
                type="monotone"
                dataKey="Short"
                name="Short"
                stroke={theme.palette.grey[400]}
                strokeWidth={1.5}
                strokeOpacity={0.5}
                dot={false}
                activeDot={{ r: 4 }}
                connectNulls
                strokeDasharray="5 5"
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default StrategyPerformanceChart;

